import { useAuth0 } from '@auth0/auth0-react';
import { getBoatName } from '@utils/getBoatName';
import { Button, Space } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';
import { NotificationInstance } from 'antd/es/notification/interface';
import { NextRouter, useRouter } from 'next/router';
import { useAntMessage, useAntNotification } from 'providers';
import { useEffect, useRef } from 'react';
import { Boat } from 'services/graphql/cms';
import { useBoatsListByIDs } from 'services/hooks/queries/useBoatsListByIDs';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export const LOCAL_STORAGE_NAME = 'boatCompare';

type StoredBoatAttributes = Pick<Boat, 'image' | 'manufacturer' | 'model' | 'year_first' | 'files'>;

export interface StoredBoat extends Pick<Boat, 'documentId'> {
  attributes: StoredBoatAttributes;
}

export interface CompareActionConfig {
  isAuthenticated?: boolean;
  message?: MessageInstance;
  router: NextRouter;
  notification?: NotificationInstance;
}

interface CompareState {
  comparableBoats: StoredBoat[];
  boatIndexToShowGallery: number | undefined;
  activeCarouselItem: number;
  initialBoatsIds: string[] | undefined;
  setActiveCarouselItem: (activeCarouselItem: number) => void;
  setBoatIndexToShowGallery: (index: number) => void;
  addToCompare: (boat: StoredBoat, config?: CompareActionConfig) => void;
  removeAllComparableBoats: () => void;
  removeBoatFromCompare: (boat: StoredBoat, config?: CompareActionConfig) => void;
  boatExistsToCompare: (boatId: string) => boolean;
  toggleCompare: (boat: StoredBoat, config?: CompareActionConfig) => void;
  setInitialBoatsIds: (initialBoatsIds: string[] | undefined) => void;
}

const showAddedWithSuccessMessage = (boat: StoredBoat, config: CompareActionConfig) => {
  const boatTitle = getBoatName(boat?.attributes);

  config.message.open({
    type: 'success',
    content: (
      <Space>
        <>
          <i>{boatTitle}</i> is added to Compare.
        </>
        <Button
          type="link"
          onClick={() => {
            config.router.push('/compare');
          }}
        >
          View all
        </Button>
      </Space>
    ),
    duration: 3,
  });
};

const showRemovedWithSuccessMessage = (boat: StoredBoat, config: CompareActionConfig) => {
  const boatTitle = getBoatName(boat?.attributes);

  config.message.open({
    type: 'success',
    content: (
      <>
        <i>{boatTitle}</i> is removed from compare.
      </>
    ),
    duration: 3,
  });
};

const showOnLimitReachedMessage = (config: CompareActionConfig) => {
  config.notification.open({
    type: 'warning',
    message: 'Please sign in to compare more than two boats!',
    duration: 2,
    placement: 'topRight',
  });
};

const useCompareStore = create<CompareState>()(
  persist(
    (set, get) => ({
      comparableBoats: [],
      boatIndexToShowGallery: undefined,
      activeCarouselItem: 0,
      initialBoatsIds: [],
      setBoatIndexToShowGallery: (boatIndexToShowGallery?: number) => set(() => ({ boatIndexToShowGallery })),
      addToCompare: (newBoat, config) => {
        const nrOfComparableBoats = get().comparableBoats.length;

        if (nrOfComparableBoats > 1 && !config?.isAuthenticated) {
          showOnLimitReachedMessage(config);
        } else if (!get().boatExistsToCompare(newBoat.documentId)) {
          const boatToBeSaved: StoredBoat = {
            documentId: newBoat.documentId,
            attributes: {
              image: newBoat.attributes.image,
              manufacturer: newBoat.attributes.manufacturer,
              model: newBoat.attributes.model,
              year_first: newBoat.attributes.year_first,
              files: newBoat.attributes.files,
            },
          };

          set((state) => ({ comparableBoats: [...state.comparableBoats, boatToBeSaved] }));
          showAddedWithSuccessMessage(newBoat, config);
        }
      },
      removeAllComparableBoats: () => set(() => ({ comparableBoats: [] })),
      setActiveCarouselItem: (activeCarouselItem: number) => set(() => ({ activeCarouselItem })),
      toggleCompare: (boat, config) => {
        if (!get().boatExistsToCompare(boat.documentId)) {
          get().addToCompare(boat, config);
        } else {
          get()?.removeBoatFromCompare(boat, config);
        }
      },
      removeBoatFromCompare: (boat, config) => {
        set((state) => ({
          comparableBoats: state.comparableBoats.filter((item) => item.documentId !== boat.documentId),
        }));
        showRemovedWithSuccessMessage(boat, config);
      },
      setInitialBoatsIds(initialBoatsIds) {
        set(() => ({ initialBoatsIds: initialBoatsIds }));
      },
      boatExistsToCompare: (boatId: string) =>
        get().comparableBoats.findIndex((item) => item.documentId === boatId) >= 0,
    }),

    {
      name: 'compare',
    },
  ),
);

export const useComparableBoats = () => useCompareStore((state) => state.comparableBoats);
export const useBoatIndexToShowGallery = () => useCompareStore((state) => state.boatIndexToShowGallery);
export const useSetBoatIndexToShowGallery = () => useCompareStore((state) => state.setBoatIndexToShowGallery);
export const useBoatExistsToCompare = () => useCompareStore((state) => state.boatExistsToCompare);
export const useRemoveAllComparableBoats = () => useCompareStore((state) => state.removeAllComparableBoats);

export const useAddToCompare = () => {
  const addToCompare = useCompareStore((state) => state.addToCompare);
  const { isAuthenticated } = useAuth0();
  const router = useRouter();
  const { notification } = useAntNotification();
  const { message } = useAntMessage();
  const config: CompareActionConfig = {
    isAuthenticated,
    router,
    notification,
    message,
  };

  return (boat: StoredBoat) => {
    addToCompare(boat, config);
  };
};

export const useRemoveBoatFromCompare = () => {
  const removeBoatFromCompare = useCompareStore((state) => state.removeBoatFromCompare);
  const { isAuthenticated } = useAuth0();
  const router = useRouter();
  const { message } = useAntMessage();

  const config: CompareActionConfig = {
    isAuthenticated,
    router,
    message,
  };

  return (boat: StoredBoat) => {
    removeBoatFromCompare(boat, config);
  };
};

export const useToggleCompare = () => {
  const toggleCompare = useCompareStore((state) => state.toggleCompare);
  const { isAuthenticated } = useAuth0();
  const router = useRouter();
  const { message } = useAntMessage();
  const { notification } = useAntNotification();

  const config: CompareActionConfig = {
    isAuthenticated,
    router,
    message,
    notification,
  };

  return (boat: StoredBoat) => {
    toggleCompare(boat, config);
  };
};

export const useGetActiveCarouselItem = () => useCompareStore((state) => state.activeCarouselItem);
export const useSetActiveCarouselItem = () => useCompareStore((state) => state.setActiveCarouselItem);
export const useGetInitialBoatIds = () => useCompareStore((state) => state.initialBoatsIds);

export const useFullComparableBoats = () => {
  const comparableBoats = useComparableBoats();
  const { setInitialBoatsIds } = useCompareStore();
  const ref = useRef<string[]>();

  useEffect(() => {
    const _ids = comparableBoats.map((item) => item.documentId);

    setInitialBoatsIds(_ids);
    ref.current = _ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparableBoats.map, setInitialBoatsIds]);

  return useBoatsListByIDs(ref.current || [], {
    boats: comparableBoats.map((item) => ({ documentId: item.documentId, ...item.attributes }) as Boat),
  });
};
